import React, { useRef } from 'react';
import { ExpandMore, UnfoldMore } from '@mui/icons-material';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { WidgetOrigin } from '../../../types/Statistics.types';
import { Order } from '../Statistics.types';
import {
  arrowStyling,
  arrowStylingDefault,
  SortableHeaderButton,
} from '../../../components/TableHeaderButton';
import type { WidgetOriginKey } from './WidgetOriginsTable';

interface WidgetOriginsTableDesktopProps {
  rows: WidgetOrigin[];
  rowOffset: number;
  total: number;
  showAccount: boolean;
  accountName: (id?: string) => string;
  onSort: (property: WidgetOriginKey) => void;
  order: Order;
  orderBy: WidgetOriginKey;
}

const ALL_COLUMNS: Array<{ key: WidgetOriginKey; label: string }> = [
  { key: 'account', label: 'statistics.table.account' },
  { key: 'host', label: 'statistics.table.host' },
  { key: 'path', label: 'statistics.table.page' },
  { key: 'count', label: 'statistics.table.noOfSearches' },
];

const colId = (key: WidgetOriginKey) => `widget-origins-col-${key}`;

export const WidgetOriginsTableDesktop: React.FC<
  WidgetOriginsTableDesktopProps
> = ({
  rows,
  rowOffset,
  total,
  showAccount,
  accountName,
  onSort,
  order,
  orderBy,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const tableRef = useRef<HTMLTableElement>(null);
  const columns = showAccount
    ? ALL_COLUMNS
    : ALL_COLUMNS.filter((column) => column.key !== 'account');

  const getArrowIcon = (key: WidgetOriginKey) => {
    if (orderBy === key) {
      return (
        <ExpandMore
          fontSize="small"
          style={order === 'asc' ? arrowStyling : arrowStylingDefault}
        />
      );
    }
    return <UnfoldMore fontSize="small" style={arrowStylingDefault} />;
  };

  const handleKeyDown = (
    event: React.KeyboardEvent,
    property: WidgetOriginKey,
    columnIndex: number
  ) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSort(property);
      return;
    }

    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;

    event.preventDefault();
    const nextIndex =
      event.key === 'ArrowRight'
        ? (columnIndex + 1) % columns.length
        : columnIndex === 0
          ? columns.length - 1
          : columnIndex - 1;

    const nextButton = tableRef.current?.querySelector(
      `thead th:nth-child(${nextIndex + 1}) button`
    ) as HTMLElement | null;
    nextButton?.focus();
  };

  return (
    <Box>
      <TableContainer
        role="group"
        tabIndex={0}
        aria-label={String(
          t('statistics.widgetOrigins.tableRegion', 'Widget search statistics')
        )}
        sx={{
          maxHeight: '60vh',
          '&:focus-visible': {
            outline: `3px solid ${theme.palette.primary.main}`,
            outlineOffset: '2px',
          },
        }}
      >
        <Table
          stickyHeader
          size="small"
          ref={tableRef}
          aria-label={String(
            t(
              'statistics.widgetOrigins.tableLabel',
              'Sortable widget search statistics'
            )
          )}
          aria-rowcount={total + 1}
        >
          <caption className="sr-only">
            {t('statistics.widgetOrigins.tableCaption', {
              count: total,
              defaultValue:
                'Widget searches per host page, {{count}} pages. Click column headers to sort.',
            })}
          </caption>
          <TableHead>
            <TableRow aria-rowindex={1}>
              {columns.map((column, columnIndex) => (
                <TableCell
                  key={column.key}
                  id={colId(column.key)}
                  sortDirection={orderBy === column.key ? order : false}
                  aria-sort={
                    orderBy === column.key
                      ? order === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : 'none'
                  }
                  scope="col"
                  align={column.key === 'count' ? 'right' : 'left'}
                >
                  <SortableHeaderButton
                    variant="secondary"
                    onClick={() => onSort(column.key)}
                    onKeyDown={(e) => handleKeyDown(e, column.key, columnIndex)}
                    isSorted={orderBy === column.key}
                    sortDirection={order}
                    sx={{
                      justifyContent:
                        column.key === 'count' ? 'flex-end' : 'left',
                    }}
                  >
                    {t(column.label)}
                    {getArrowIcon(column.key)}
                  </SortableHeaderButton>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => {
              const rowId = `widget-origins-row-${index}`;
              return (
                <TableRow
                  key={`${row.account ?? ''}-${row.host}${row.path}`}
                  hover
                  aria-rowindex={rowOffset + index + 2}
                >
                  {showAccount && (
                    <TableCell headers={`${colId('account')} ${rowId}`}>
                      {accountName(row.account)}
                    </TableCell>
                  )}
                  <TableCell headers={`${colId('host')} ${rowId}`}>
                    {row.host}
                  </TableCell>
                  <TableCell
                    component="th"
                    scope="row"
                    id={rowId}
                    headers={colId('path')}
                    sx={{ fontWeight: 'normal', overflowWrap: 'anywhere' }}
                  >
                    {/* Plain text, not a link: the host comes from a header a
                        non-browser client can forge. */}
                    <span className="sr-only">
                      {showAccount && `${accountName(row.account)}, `}
                      {row.host}
                    </span>
                    {row.path}
                  </TableCell>
                  <TableCell
                    headers={`${colId('count')} ${rowId}`}
                    align="right"
                  >
                    {row.count.toLocaleString()}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
